import { inject } from '@angular/core';
import { RedirectFunction, Route, Router } from '@angular/router';

import { subscriptionCheckoutGuard } from './guards/subscription-checkout.guard';
import { subscriptionCompleteUnloadGuard } from './guards/subscription-complete-unload.guard';
import { subscriptionCompleteGuard } from './guards/subscription-complete.guard';
import { subscriptionErrorGuard } from './guards/subscription-error.guard';
import { subscriptionProcessingUnloadGuard } from './guards/subscription-processing-unload.guard';
import { subscriptionProcessingGuard } from './guards/subscription-processing.guard';
import { subscriptionSuccessGuard } from './guards/subscription-success.guard';

/**
 * Billing is where upgrading happens now.
 *
 * `/subscription/upgrade` is the old plans page, which the redesign has not reached — every paywall,
 * guard and inline "Upgrade" link in the app still points at it, so the redirect lives HERE rather
 * than being chased across a dozen templates: one line moves them all, and one line moves them back
 * when the page is redesigned. Billing carries the same offer (the plan picker) and hands off to the
 * same checkout, so nothing is lost in the diversion.
 *
 * The redirect is a **function**, not a plain string. Angular builds a string `redirectTo`'s query
 * params from the `redirectTo` string itself, never from the incoming URL — so a plain
 * `redirectTo: '/billing'` silently drops `?requiredTier=STARTER|PRO`, the one thing the paywall
 * interceptor and every upgrade-wall CTA rely on to preselect a plan once the operator lands on Billing
 * (see `docs/design/features/billing.md`, "Preselection"). A function-based `redirectTo` runs in an
 * injection context and is handed the in-flight `queryParams` directly, so it rebuilds the `/billing`
 * `UrlTree` carrying them across untouched.
 */
const UPGRADE_HOME = '/billing';

const redirectToBilling: RedirectFunction = (redirectData) =>
  inject(Router).createUrlTree([UPGRADE_HOME], { queryParams: redirectData.queryParams });

export const SUBSCRIPTION_ROUTES: Route[] = [
  {
    path: '',
    redirectTo: redirectToBilling,
    pathMatch: 'full',
  },
  {
    // The PlansPage component is parked, not deleted: it comes back under a redesign, not a revert.
    path: 'upgrade',
    redirectTo: redirectToBilling,
  },
  {
    path: 'checkout',
    title: 'Checkout',
    loadComponent: () =>
      import('./pages/subscription-checkout/subscription-checkout.page').then((c) => c.SubscriptionCheckoutPage),
    canActivate: [subscriptionCheckoutGuard],
  },
  {
    path: 'complete',
    title: 'Verifying your subscription',
    loadComponent: () =>
      import('./pages/subscription-complete/subscription-complete.page').then((c) => c.SubscriptionCompletePage),
    canActivate: [subscriptionCompleteGuard],
    canDeactivate: [subscriptionCompleteUnloadGuard],
  },
  {
    path: 'processing',
    title: 'Activating your subscription',
    loadComponent: () =>
      import('./pages/subscription-processing/subscription-processing.page').then((c) => c.SubscriptionProcessingPage),
    canActivate: [subscriptionProcessingGuard],
    canDeactivate: [subscriptionProcessingUnloadGuard],
  },
  {
    path: 'success',
    title: 'Subscription confirmed',
    loadComponent: () =>
      import('./pages/subscription-success/subscription-success.page').then((c) => c.SubscriptionSuccessPage),
    canActivate: [subscriptionSuccessGuard],
  },
  {
    path: 'error',
    title: 'Subscription problem',
    loadComponent: () =>
      import('./pages/subscription-error/subscription-error.page').then((c) => c.SubscriptionErrorPage),
    canActivate: [subscriptionErrorGuard],
  },
];
